import type { Link } from '@readium/shared';
import type { OfflineResourceSource } from './CustomFetch';

const XHTML_TYPE = 'application/xhtml+xml';
const HTML_TYPE = 'text/html';
const XLINK_NS = 'http://www.w3.org/1999/xlink';
/** `@import` chains deeper than this are left as they are. */
const MAX_CSS_DEPTH = 3;

/** Elements and attributes of a chapter that point to a book file. */
const URL_ATTRIBUTES: readonly (readonly [selector: string, attribute: string])[] = [
  ['img[src]', 'src'],
  ['source[src]', 'src'],
  ['audio[src]', 'src'],
  ['video[src]', 'src'],
  ['video[poster]', 'poster'],
  ['track[src]', 'src'],
  ['object[data]', 'data'],
  ['image[href]', 'href'],
  ['use[href]', 'href'],
];
const CSS_URL = /url\(\s*(['"]?)([^'")]+)\1\s*\)/gu;
const CSS_IMPORT = /@import\s+(['"])([^'"]+)\1/gu;

/**
 * Chapter iframes are `blob:` documents: they are not controlled by the service worker, so their
 * images, styles and fonts cannot be served from the offline cache by URL. In offline sessions
 * every reference to a stored book file is replaced with a `blob:` URL of the stored copy
 * (the frame CSP already allows `blob:`), including the `url()`s inside the stylesheets.
 */
export class OfflineResourceInliner {
  private readonly source: OfflineResourceSource;
  private readonly filesBaseUrl: string;
  private readonly bookFilesUrl: string;
  private readonly blobUrls = new Map<string, Promise<string | undefined>>();

  /**
   * @param filesBaseUrl Base the chapters are resolved against (Readium's `<base>`).
   * @param bookFilesUrl Every file of the book lives under it; other URLs are left untouched.
   */
  constructor(source: OfflineResourceSource, filesBaseUrl: string, bookFilesUrl: string) {
    this.source = source;
    this.filesBaseUrl = filesBaseUrl;
    this.bookFilesUrl = bookFilesUrl;
  }

  async inlineChapter(html: string, link: Link): Promise<string> {
    const documentUrl = link.toURL(this.filesBaseUrl);
    if (!documentUrl) return html;
    const type = link.mediaType.string === XHTML_TYPE ? XHTML_TYPE : HTML_TYPE;
    const doc = new DOMParser().parseFromString(html, type);
    if (doc.querySelector('parsererror')) return html;

    await Promise.all([
      ...URL_ATTRIBUTES.flatMap(([selector, attribute]) =>
        [...doc.querySelectorAll(selector)].map(async (element) => {
          await this.replaceAttribute(element, attribute, documentUrl);
        }),
      ),
      ...[...doc.querySelectorAll('image, use')].map(async (element) => {
        const value = element.getAttributeNS(XLINK_NS, 'href');
        const blobUrl = value ? await this.blobUrl(value, documentUrl) : undefined;
        if (blobUrl) element.setAttributeNS(XLINK_NS, 'xlink:href', blobUrl);
      }),
      ...[...doc.querySelectorAll('img[srcset], source[srcset]')].map(async (element) => {
        element.setAttribute(
          'srcset',
          await this.rewriteSrcset(element.getAttribute('srcset') ?? '', documentUrl),
        );
      }),
      ...[...doc.querySelectorAll('link[href]')]
        .filter((element) => /\bstylesheet\b/iu.test(element.getAttribute('rel') ?? ''))
        .map(async (element) => {
          await this.replaceAttribute(element, 'href', documentUrl);
        }),
      ...[...doc.querySelectorAll('style')].map(async (element) => {
        element.textContent = await this.rewriteCss(element.textContent, documentUrl, 0);
      }),
      ...[...doc.querySelectorAll('[style]')].map(async (element) => {
        const css = element.getAttribute('style') ?? '';
        element.setAttribute('style', await this.rewriteCss(css, documentUrl, 0));
      }),
    ]);
    return new XMLSerializer().serializeToString(doc);
  }

  private async replaceAttribute(
    element: Element,
    attribute: string,
    documentUrl: string,
  ): Promise<void> {
    const value = element.getAttribute(attribute);
    const blobUrl = value ? await this.blobUrl(value, documentUrl) : undefined;
    if (blobUrl) element.setAttribute(attribute, blobUrl);
  }

  private async rewriteSrcset(srcset: string, documentUrl: string): Promise<string> {
    const candidates = await Promise.all(
      srcset.split(',').map(async (candidate) => {
        const [url = '', ...descriptors] = candidate.trim().split(/\s+/u);
        const blobUrl = url ? await this.blobUrl(url, documentUrl) : undefined;
        return [blobUrl ?? url, ...descriptors].join(' ');
      }),
    );
    return candidates.join(', ');
  }

  private async rewriteCss(css: string, cssUrl: string, depth: number): Promise<string> {
    const references = [...css.matchAll(CSS_URL), ...css.matchAll(CSS_IMPORT)];
    const replacements = await Promise.all(
      references.map(async ([match, , url = '']) => {
        const blobUrl = await this.blobUrl(url, cssUrl, depth);
        return [match, blobUrl ? match.replace(url, blobUrl) : match] as const;
      }),
    );
    return replacements.reduce((text, [from, to]) => text.replaceAll(from, to), css);
  }

  /** `blob:` URL of the stored copy of `reference`, or undefined to keep the reference. */
  private async blobUrl(
    reference: string,
    baseUrl: string,
    depth = 0,
  ): Promise<string | undefined> {
    if (/^(?:blob|data|#)/iu.test(reference) || !URL.canParse(reference, baseUrl)) {
      return undefined;
    }
    const url = new URL(reference, baseUrl);
    const { hash } = url;
    url.hash = '';
    if (!url.href.startsWith(this.bookFilesUrl)) return undefined;
    let blobUrl = this.blobUrls.get(url.href);
    if (!blobUrl) {
      blobUrl = this.storedBlobUrl(url.href, depth);
      this.blobUrls.set(url.href, blobUrl);
    }
    const stored = await blobUrl;
    // Keep fragments such as SVG sprite ids (`icons.svg#star`).
    return stored ? `${stored}${hash}` : undefined;
  }

  private async storedBlobUrl(url: string, depth: number): Promise<string | undefined> {
    const response = await this.source(url).catch(() => undefined);
    if (!response?.ok) return undefined;
    const blob = await response.blob();
    if (blob.type.includes('css') || url.toLowerCase().endsWith('.css')) {
      const css = await blob.text();
      const rewritten = depth < MAX_CSS_DEPTH ? await this.rewriteCss(css, url, depth + 1) : css;
      return URL.createObjectURL(new Blob([rewritten], { type: 'text/css' }));
    }
    return URL.createObjectURL(blob);
  }
}
